<script lang="ts">
  // ============================================================
  import { router } from '@inertiajs/svelte';
  import { toasts } from '@/stores/toasts';
  import { api } from '@/utils/api';

  interface User {
    id: number;
    email: string;
    workEmail?: string;
    name: string;
    nickname?: string;
    username?: string;
    organization?: string;
    department?: string;
    team?: string;
    position?: string;
    companyDomain?: string;
    companyVerificationStatus?: 'verified' | 'unverified' | 'restricted';
    companyVerifiedAt?: string;
    companyRestrictedAt?: string;
    companyAccessBlocked?: boolean;
    manualCompanyAccessBlocked?: boolean;
    domainAuthority?: string;
    domainLoginAllowed?: boolean;
    domainVerificationEnabled?: boolean;
    inviteCode?: string;
    inviteLink?: string;
    verificationDue?: boolean;
    nextReverificationAt?: string;
  }

  export let user: User = {} as User;
  export let errors: Record<string, string[]> = {};
  export let flash: Record<string, string> = {};

  import { onMount } from 'svelte';

  let nickname = user.nickname || '';
  let username = user.username || '';
  let isSubmitting = false;
  let isReverifySubmitting = false;

  // 비밀번호 변경
  let showPasswordModal = false;
  let currentPassword = '';
  let newPassword = '';
  let newPasswordConfirmation = '';
  let isPasswordSubmitting = false;

  // 회사 이메일 변경 모달
  let showChangeEmailModal = false;
  let newWorkEmail = '';
  let isChangeEmailSubmitting = false;

  // 회사 인증 초기화 확인 모달
  let showResetModal = false;
  let isResetSubmitting = false;

  // 계정 삭제 확인 모달
  //
  // 🔴 스토어 심사 필수 경로다. Apple 5.1.1(v) 는 앱 안에서 삭제가 끝나야 하고,
  //    Play 는 앱 밖(웹)에서도 삭제를 요청할 수 있어야 한다 — 이 화면이 후자다.
  //    앱(iOS/Android)은 같은 `DELETE /api/v1/account` 를 부른다.
  let showDeleteAccountModal = false;
  let deleteConfirmText = '';
  let isDeletingAccount = false;
  const DELETE_CONFIRM_PHRASE = '계정 삭제';

  onMount(() => {
    if (window.location.hash === '#company-verification') {
      const el = document.getElementById('company-verification');
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  });

  function requestReverification() {
    isReverifySubmitting = true;
    router.post(
      '/settings/reverify',
      {},
      {
        onFinish: () => {
          isReverifySubmitting = false;
        },
      }
    );
  }

  function submitChangePassword() {
    if (!currentPassword || !newPassword || !newPasswordConfirmation) return;
    isPasswordSubmitting = true;
    router.post(
      '/settings/change_password',
      {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: newPasswordConfirmation,
      },
      {
        onFinish: () => {
          isPasswordSubmitting = false;
          showPasswordModal = false;
          currentPassword = '';
          newPassword = '';
          newPasswordConfirmation = '';
        },
      }
    );
  }

  function submitChangeWorkEmail() {
    if (!newWorkEmail.trim()) return;
    isChangeEmailSubmitting = true;
    router.post(
      '/settings/update_work_email',
      { work_email: newWorkEmail.trim() },
      {
        onFinish: () => {
          isChangeEmailSubmitting = false;
          showChangeEmailModal = false;
          newWorkEmail = '';
        },
      }
    );
  }

  function submitResetCompanyVerification() {
    isResetSubmitting = true;
    router.post(
      '/settings/reset_company_verification',
      {},
      {
        onFinish: () => {
          isResetSubmitting = false;
          showResetModal = false;
        },
      }
    );
  }

  // 🔴 삭제 후에는 어떤 화면도 다시 그리지 않는다. 서버가 세션 쿠키를 지우고
  //    자격증명을 전부 폐기하므로, Inertia 로 이동하면 그 요청이 401 로 튕겨
  //    "실패한 것처럼" 보인다. 전체 새로고침으로 로그인 화면에 내려놓는다.
  async function submitDeleteAccount() {
    if (deleteConfirmText.trim() !== DELETE_CONFIRM_PHRASE) return;
    isDeletingAccount = true;

    const response = await api.delete('/api/v1/account');

    // 🔴 HTTP 성공만으로 삭제됐다고 말하지 않는다. 서버가 200 을 주면서
    //    `deleted: false` 를 내려도 이 화면이 "삭제했습니다" 로 안내하면,
    //    사용자는 지웠다고 믿고 계정은 살아 있다. iOS·Android 클라이언트가
    //    같은 값을 같은 기준으로 본다(`deleted` 를 성공 판정에 쓴다).
    //    응답 봉투는 `{data: {...}}` 이고 클라이언트가 그것을 펼쳐 주므로
    //    읽는 자리는 `response.data` 다.
    if (response.success && (response.data as { deleted?: boolean } | undefined)?.deleted) {
      window.location.href = '/login?deleted=1';
      return;
    }

    isDeletingAccount = false;
    // 이 클라이언트의 실패 응답은 `error` 가 문자열이다(ProfileEditor 계열과 동일 취급).
    const message = typeof response.error === 'string' && response.error ? response.error : '';
    toasts.error(message || '계정 삭제에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    deleteConfirmText = '';
  }

  async function copyInviteLink() {
    if (!user.inviteLink) return;

    try {
      await navigator.clipboard.writeText(user.inviteLink);
      toasts.success('초대 링크를 복사했습니다.');
    } catch {
      toasts.error('클립보드 복사에 실패했습니다. 수동으로 복사해 주세요.');
    }
  }

  function companyStatusLabel(status?: string) {
    switch (status) {
      case 'verified':
        return '인증 완료';
      case 'restricted':
        return '이용 제한';
      default:
        return '미인증';
    }
  }

  function companyStatusClass(status?: string) {
    switch (status) {
      case 'verified':
        return 'badge badge-success';
      case 'restricted':
        return 'badge badge-error';
      default:
        return 'badge badge-neutral';
    }
  }

  function handleSubmit(e: Event) {
    e.preventDefault();
    isSubmitting = true;

    router.patch(
      '/settings',
      {
        user: { nickname, username: username || undefined },
      },
      {
        onFinish: () => {
          isSubmitting = false;
        },
      }
    );
  }
</script>

<div class="space-y-6">
    <!-- Page Header -->
    <div class="mb-6">
      <h1 class="text-h1 text-content">설정</h1>
      <p class="text-body2 text-content-secondary mt-1">
        프로필 및 개인정보 설정을 관리하세요
      </p>
    </div>

    <div class="max-w-6xl mx-auto">
      <!-- Flash Messages -->
      {#if flash.notice}
        <div
          class="mb-6 p-4 bg-success-light dark:bg-success/20 border border-success/30 rounded-xl text-success-dark dark:text-success text-body2"
        >
          {flash.notice}
        </div>
      {/if}

      <div class="grid grid-cols-1 lg:grid-cols-[320px_1fr] xl:grid-cols-[360px_1fr] gap-6">
        <!-- Profile Info -->
        <div>
          <div class="card p-6">
            <h2 class="text-label text-content mb-4">프로필 정보</h2>

            <div class="space-y-3">
              <div>
                <p class="text-caption text-content-tertiary">이름</p>
                <p class="text-body2 text-content font-medium">
                  {user.name || '미입력'}
                </p>
              </div>

              <div>
                <p class="text-caption text-content-tertiary">이메일</p>
                <p class="text-body2 text-content font-medium break-all">
                  {user.email}
                </p>
              </div>

              {#if user.organization}
                <div>
                  <p class="text-caption text-content-tertiary">소속</p>
                  <p class="text-body2 text-content font-medium">
                    {user.organization}
                  </p>
                </div>
              {/if}

              {#if user.department}
                <div>
                  <p class="text-caption text-content-tertiary">부서</p>
                  <p class="text-body2 text-content font-medium">
                    {user.department}
                  </p>
                </div>
              {/if}

              {#if user.team}
                <div>
                  <p class="text-caption text-content-tertiary">팀</p>
                  <p class="text-body2 text-content font-medium">
                    {user.team}
                  </p>
                </div>
              {/if}

              {#if user.position}
                <div>
                  <p class="text-caption text-content-tertiary">직책</p>
                  <p class="text-body2 text-content font-medium">
                    {user.position}
                  </p>
                </div>
              {/if}
            </div>

            <!-- Password Change -->
            <div class="mt-4 pt-4 border-t border-gray-200 dark:border-dark-border">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-label text-content">비밀번호</p>
                  <p class="text-caption text-content-tertiary mt-0.5">마지막 변경: 알 수 없음</p>
                </div>
                <button
                  class="btn btn-secondary btn-sm"
                  onclick={() => { showPasswordModal = true; }}
                >
                  변경
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Settings Form -->
        <div>
          <form onsubmit={handleSubmit} class="space-y-6">
            <!--
              🔑 Username vs Nickname 차이:
              - username: 공개 프로필 URL에 사용 (axhub.space/@username). 고유값, 영문 소문자만.
                         Linktree의 linktr.ee/username과 동일한 역할.
              - nickname: 커뮤니티 내 익명/반익명 활동 시 표시되는 별명. 중복 허용, 한글 가능.
            -->

            <!-- Username (Public Profile URL) Setting -->
            <div class="card p-6">
              <h2 class="text-label text-content mb-4">공개 프로필 링크</h2>
              <p class="text-body2 text-content-tertiary mb-4">
                나만의 공개 프로필 페이지 주소를 설정하세요. 외부에 공유할 수 있는 링크입니다.
              </p>

              <div class="mb-4">
                <label
                  for="username"
                  class="block text-label text-gray-700 dark:text-dark-text-medium mb-2"
                >
                  사용자명 (영문 소문자, 숫자, 밑줄)
                </label>
                <div class="flex items-center gap-2">
                  <span class="text-body2 text-gray-400 dark:text-dark-text-disabled shrink-0">axhub.space/@</span>
                  <input
                    type="text"
                    id="username"
                    bind:value={username}
                    maxlength="30"
                    pattern="[a-z0-9_]+"
                    class="input flex-1"
                    placeholder="seunghan"
                  />
                </div>
                {#if errors.username}
                  <p class="mt-1 text-caption text-error">{errors.username}</p>
                {/if}
                {#if username}
                  <p class="mt-2 text-body2 text-content-tertiary">
                    내 프로필: <a href="/@{username}" class="text-primary hover:underline" target="_blank">axhub.space/@{username}</a>
                  </p>
                  <div class="mt-3 p-3 bg-primary/5 dark:bg-primary/10 border border-primary/20 rounded-lg space-y-2">
                    <p class="text-body2 text-gray-700 dark:text-dark-text-medium">
                      🎨 프로필 페이지를 꾸미고 싶으세요?
                      <a href="/my-profile/editor/design" class="text-primary font-semibold hover:underline ml-1">프로필 에디터 열기 →</a>
                    </p>
                    <p class="text-body2 text-gray-700 dark:text-dark-text-medium">
                      📊 발표자료를 관리하고 싶으세요?
                      <a href="/my-profile/presentations" class="text-primary font-semibold hover:underline ml-1">발표자료 관리 →</a>
                    </p>
                    <p class="text-caption text-content-tertiary">
                      링크 추가, 테마 변경, 발표자료 업로드 등 프로필을 꾸밀 수 있습니다.
                    </p>
                  </div>
                {/if}
                <p class="mt-2 text-caption text-gray-400 dark:text-dark-text-disabled">
                  3-30자, 영문 소문자·숫자·밑줄만 가능. 30일에 1번 변경 가능.
                </p>
              </div>
            </div>

            <!-- Nickname Setting (커뮤니티 내 표시 이름, username과 다름) -->
            <div class="card p-6">
              <h2 class="text-label text-content mb-4">닉네임 설정</h2>
              <p class="text-body2 text-content-tertiary mb-4">
                커뮤니티에서 익명/반익명 활동 시 표시되는 이름입니다. 공개 프로필 주소(위 사용자명)와는 다릅니다.
              </p>

              <div class="mb-4">
                <label
                  for="nickname"
                  class="block text-label text-gray-700 dark:text-dark-text-medium mb-2"
                >
                  닉네임
                </label>
                <input
                  type="text"
                  id="nickname"
                  bind:value={nickname}
                  maxlength="20"
                  class="input"
                  placeholder="1-20자의 닉네임을 입력하세요"
                />
                {#if errors.nickname}
                  <p class="mt-1 text-caption text-error">{errors.nickname}</p>
                {/if}
                <p class="mt-1 text-caption text-gray-400 dark:text-dark-text-disabled text-right">
                  {nickname.length}/20
                </p>
              </div>

              <p
                class="text-body2 text-content-secondary bg-info-light dark:bg-info/10 border border-info/20 rounded-lg p-3"
              >
                <svg
                  class="w-4 h-4 text-info inline-block mr-1 -mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  ><path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  /></svg
                >
                <span class="font-medium">닉네임 사용 안내:</span><br />
                • <strong>회사만 공개</strong> 시 닉네임 표시<br />
                • <strong>완전 익명</strong> 시 닉네임 또는 "익명 사용자" 표시
              </p>

              <div class="flex justify-end mt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  class="btn btn-primary btn-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>
          </form>

          <!-- Privacy Level Guide -->
          <div class="card p-6">
            <h2 class="text-label text-content mb-4">공개 범위 안내</h2>

            <div class="space-y-3">
              <!-- Full Disclosure -->
              <div
                class="flex gap-3 pb-4 border-b border-default last:pb-0 last:border-b-0"
              >
                <div
                  class="flex-shrink-0 w-8 h-8 bg-primary-50 dark:bg-primary/15 rounded-lg flex items-center justify-center"
                >
                  <svg
                    class="w-4 h-4 text-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    ><path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    /></svg
                  >
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-label text-content font-medium">
                    전체 공개
                  </p>
                  <p class="text-caption text-content-secondary mt-0.5">
                    이름 · 소속 · 부서 모두 표시
                  </p>
                </div>
              </div>

              <!-- Company Only -->
              <div
                class="flex gap-3 pb-4 border-b border-default last:pb-0 last:border-b-0"
              >
                <div
                  class="flex-shrink-0 w-8 h-8 bg-info-light dark:bg-info/15 rounded-lg flex items-center justify-center"
                >
                  <svg
                    class="w-4 h-4 text-info"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    ><path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    /></svg
                  >
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-label text-content font-medium">
                    회사만 공개
                  </p>
                  <p class="text-caption text-content-secondary mt-0.5">
                    닉네임 · 소속 · 부서 표시
                  </p>
                </div>
              </div>

              <!-- Name Only -->
              <div
                class="flex gap-3 pb-4 border-b border-default last:pb-0 last:border-b-0"
              >
                <div
                  class="flex-shrink-0 w-8 h-8 bg-success-light dark:bg-success/15 rounded-lg flex items-center justify-center"
                >
                  <svg
                    class="w-4 h-4 text-success"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    ><path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    /></svg
                  >
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-label text-content font-medium">
                    이름만 공개
                  </p>
                  <p class="text-caption text-content-secondary mt-0.5">
                    이름만 표시, 소속·부서 미표시
                  </p>
                </div>
              </div>

              <!-- Anonymous -->
              <div
                class="flex gap-3 pb-4 border-b border-default last:pb-0 last:border-b-0"
              >
                <div
                  class="flex-shrink-0 w-8 h-8 bg-surface-tertiary rounded-lg flex items-center justify-center"
                >
                  <svg
                    class="w-4 h-4 text-content-secondary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    ><path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    /></svg
                  >
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-label text-content font-medium">
                    완전 익명
                  </p>
                  <p class="text-caption text-content-secondary mt-0.5">
                    닉네임 표시 (없으면 "익명 사용자")
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Company Verification -->
          <div class="card p-6" id="company-verification">
            <h2 class="text-label text-content mb-4">회사 인증 상태</h2>
            <div class="space-y-3">
              <div class="flex justify-between items-center gap-3">
                <p class="text-caption text-content-tertiary">
                  회사 이메일
                </p>
                <p class="text-body2 text-content font-mono">
                  {user.workEmail || '-'}
                </p>
              </div>
              <div class="flex justify-between items-center gap-3">
                <p class="text-caption text-content-tertiary">
                  회사 인증 기준 도메인
                </p>
                <p class="text-body2 text-content font-mono">
                  {user.companyDomain || '-'}
                </p>
              </div>
              <div class="flex justify-between items-center gap-3">
                <p class="text-caption text-content-tertiary">인증 기관</p>
                <p class="text-body2 text-content font-medium">
                  {user.domainAuthority || '미등록 도메인'}
                </p>
              </div>
              <div class="flex justify-between items-center gap-3">
                <p class="text-caption text-content-tertiary">인증 상태</p>
                <span class={companyStatusClass(user.companyVerificationStatus)}
                  >{companyStatusLabel(user.companyVerificationStatus)}</span
                >
              </div>
              {#if user.companyVerifiedAt}
                <div class="flex justify-between items-center gap-3">
                  <p class="text-caption text-content-tertiary">인증 일시</p>
                  <p class="text-body2 text-content">
                    {new Date(user.companyVerifiedAt).toLocaleString('ko-KR')}
                  </p>
                </div>
              {/if}
              {#if user.companyRestrictedAt}
                <div class="flex justify-between items-center gap-3">
                  <p class="text-caption text-content-tertiary">제한 일시</p>
                  <p class="text-body2 text-error">
                    {new Date(user.companyRestrictedAt).toLocaleString('ko-KR')}
                  </p>
                </div>
              {/if}
            </div>

            {#if user.companyVerificationStatus === 'restricted'}
              <p
                class="mt-4 text-body2 text-error bg-error-light dark:bg-error/10 border border-error/20 rounded-lg p-3"
              >
                현재 도메인 정책으로 서비스 이용이 제한되어 있습니다. 관리자에게 문의해 주세요.
              </p>
            {:else if user.companyVerificationStatus !== 'verified'}
              <p
                class="mt-4 text-body2 text-content-secondary bg-info-light dark:bg-info/10 border border-info/20 rounded-lg p-3"
              >
                회사 이메일 도메인에 해당하는 인증기관이 등록되면 자동으로 회사 인증 상태가 반영됩니다.
              </p>

              <div class="mt-4 pt-4 border-t border-default">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <p class="text-label text-content">
                      회사 이메일 인증
                    </p>
                    <p class="text-caption text-content-tertiary mt-1">
                      {user.workEmail
                        ? `${user.workEmail}로 인증 메일을 다시 보낼 수 있습니다.`
                        : '회사 이메일이 등록되어 있어야 인증 메일을 보낼 수 있습니다.'}
                    </p>
                  </div>
                  {#if user.workEmail}
                    <button
                      class="btn btn-primary btn-sm"
                      disabled={isReverifySubmitting}
                      onclick={requestReverification}
                    >
                      {isReverifySubmitting ? '발송 중...' : '인증하기'}
                    </button>
                  {:else}
                    <a class="btn btn-secondary btn-sm" href="/verification">회사 이메일 입력</a>
                  {/if}
                </div>
              </div>
            {/if}

            {#if user.companyVerificationStatus === 'verified'}
              <div class="mt-4 pt-4 border-t border-default">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <p class="text-label text-content">
                      6개월 주기 재직 인증
                    </p>
                    <p class="text-caption text-content-tertiary mt-1">
                      다음 예정일: {user.nextReverificationAt
                        ? new Date(user.nextReverificationAt).toLocaleDateString('ko-KR')
                        : '-'}
                    </p>
                  </div>
                  <button
                    class="btn {user.verificationDue ? 'btn-primary' : 'btn-secondary'} btn-sm"
                    disabled={isReverifySubmitting}
                    onclick={requestReverification}
                  >
                    {isReverifySubmitting ? '발송 중...' : '재인증 메일 보내기'}
                  </button>
                </div>
                {#if user.verificationDue}
                  <p
                    class="mt-3 text-caption text-warning-700 dark:text-warning bg-warning-50 dark:bg-warning/15 border border-warning/30 rounded-lg p-2.5"
                  >
                    재인증 권장 시점을 지났습니다. 메일을 발송해 재인증을 진행해 주세요.
                  </p>
                {/if}
              </div>
            {/if}

            <!-- 회사 이메일 변경 / 인증 초기화 -->
            {#if user.workEmail && !user.manualCompanyAccessBlocked}
              <div class="mt-4 pt-4 border-t border-default space-y-3">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <p class="text-label text-content">회사 이메일 변경</p>
                    <p class="text-caption text-content-tertiary mt-1">
                      이직 등으로 회사가 변경된 경우 새로운 회사 이메일로 재인증할 수 있습니다.
                    </p>
                  </div>
                  <button
                    class="btn btn-secondary btn-sm shrink-0"
                    onclick={() => { showChangeEmailModal = true; newWorkEmail = ''; }}
                  >
                    이메일 변경
                  </button>
                </div>
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <p class="text-label text-content">회사 인증 초기화</p>
                    <p class="text-caption text-content-tertiary mt-1">
                      회사 이메일과 인증 상태를 모두 초기화합니다.
                    </p>
                  </div>
                  <button
                    class="btn btn-secondary btn-sm text-error shrink-0"
                    onclick={() => { showResetModal = true; }}
                  >
                    초기화
                  </button>
                </div>
              </div>
            {/if}
          </div>

          <div class="card p-6">
            <h2 class="text-label text-content mb-4">초대 코드</h2>
            <div class="space-y-3">
              <div class="flex justify-between items-center gap-3">
                <p class="text-caption text-content-tertiary">내 코드</p>
                <p class="text-body2 font-mono text-content">
                  {user.inviteCode || '-'}
                </p>
              </div>
              <div>
                <p class="text-caption text-content-tertiary mb-1">초대 링크</p>
                <p class="text-body2 text-content break-all">
                  {user.inviteLink || '-'}
                </p>
              </div>
            </div>
            <div class="mt-4 flex gap-2">
              <button class="btn btn-secondary btn-sm" onclick={copyInviteLink}> 링크 복사 </button>
            </div>
          </div>

          <!-- Profile Settings Link -->
          <div class="card p-6">
            <h2 class="text-label text-content mb-4">공개 프로필</h2>
            <p class="text-body2 text-content-secondary mb-4">
              공개 프로필, 테마, 링크, Short URL을 관리합니다.
            </p>
            <a href="/settings/profile" class="btn btn-secondary btn-sm"> 프로필 설정 </a>
          </div>

          <!-- Blocked Users Link -->
          <div class="card p-6">
            <h2 class="text-label text-content mb-4">차단 관리</h2>
            <p class="text-body2 text-content-secondary mb-4">
              차단한 사용자를 확인하고 관리합니다.
            </p>
            <a href="/settings/blocked" class="btn btn-secondary btn-sm">
              <svg
                class="w-4 h-4 mr-2 inline"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                />
              </svg>
              차단 관리
            </a>
          </div>

          <!-- Hidden Conversations Link -->
          <div class="card p-6">
            <h2 class="text-label text-content mb-4">숨김 대화</h2>
            <p class="text-body2 text-content-secondary mb-4">
              인박스에서 숨긴 대화를 다시 표시할 수 있습니다.
            </p>
            <a href="/settings/hidden_conversations" class="btn btn-secondary btn-sm">
              <svg
                class="w-4 h-4 mr-2 inline"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                />
              </svg>
              숨김 대화 관리
            </a>
          </div>

          <!-- 계정 삭제 (스토어 심사 필수 경로) -->
          <div class="card p-6 border border-error/30">
            <h2 class="text-label text-error mb-4">계정 삭제</h2>
            <p class="text-body2 text-content-secondary mb-4">
              계정을 삭제하면 즉시 로그아웃되고 모든 기기의 로그인이 끊깁니다.<br />
              6개월 안에 다시 로그인하지 않으면 작성한 글과 댓글까지 영구 삭제됩니다.
            </p>
            <button
              class="btn btn-sm bg-error text-white hover:bg-error/90"
              onclick={() => { showDeleteAccountModal = true; deleteConfirmText = ''; }}
            >
              계정 삭제
            </button>
          </div>
        </div>
      </div>

      <!-- Back Navigation -->
      <div class="flex justify-center mt-8">
        <a href="/dashboard" class="btn btn-secondary btn-md">
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          대시보드로 돌아가기
        </a>
      </div>
    </div>
  </div>

<!-- 회사 이메일 변경 모달 -->
{#if showChangeEmailModal}
  <div
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    onclick={(e) => { if (e.target === e.currentTarget) showChangeEmailModal = false; }}
    role="dialog"
    aria-modal="true"
    aria-labelledby="change-email-title"
  >
    <div class="bg-surface rounded-2xl shadow-xl max-w-md w-full p-6">
      <h3 id="change-email-title" class="text-h3 text-content mb-2">
        회사 이메일 변경
      </h3>
      <p class="text-body2 text-content-secondary mb-4">
        새로운 회사 이메일을 입력하면 인증 메일이 발송되며, 기존 인증 상태는 초기화됩니다.
      </p>

      {#if user.workEmail}
        <p class="text-caption text-content-tertiary mb-3">
          현재: <span class="font-mono">{user.workEmail}</span>
        </p>
      {/if}

      <form onsubmit={(e) => { e.preventDefault(); submitChangeWorkEmail(); }}>
        <input
          type="email"
          bind:value={newWorkEmail}
          placeholder="새 회사 이메일 입력"
          class="w-full px-4 py-2.5 border border-gray-300 dark:border-dark-border rounded-xl bg-surface text-content text-body2 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          required
        />
        <div class="flex justify-end gap-2 mt-4">
          <button
            type="button"
            class="btn btn-secondary btn-sm"
            onclick={() => { showChangeEmailModal = false; }}
          >
            취소
          </button>
          <button
            type="submit"
            class="btn btn-primary btn-sm"
            disabled={isChangeEmailSubmitting || !newWorkEmail.trim()}
          >
            {isChangeEmailSubmitting ? '처리 중...' : '변경 및 인증 메일 발송'}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}

<!-- 회사 인증 초기화 확인 모달 -->
{#if showResetModal}
  <div
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    onclick={(e) => { if (e.target === e.currentTarget) showResetModal = false; }}
    role="dialog"
    aria-modal="true"
    aria-labelledby="reset-title"
  >
    <div class="bg-surface rounded-2xl shadow-xl max-w-md w-full p-6">
      <h3 id="reset-title" class="text-h3 text-content mb-2">
        회사 인증 초기화
      </h3>
      <p class="text-body2 text-content-secondary mb-4">
        회사 이메일, 도메인, 인증 상태가 모두 초기화됩니다. 초기화 후 다시 회사 이메일을 등록하고 인증해야 합니다.
      </p>
      <div class="bg-warning-50 dark:bg-warning/15 border border-warning/30 rounded-lg p-3 mb-4">
        <p class="text-caption text-warning-700 dark:text-warning">
          이 작업은 되돌릴 수 없습니다. 회사 인증이 필요한 기능의 접근 권한이 해제됩니다.
        </p>
      </div>
      <div class="flex justify-end gap-2">
        <button
          class="btn btn-secondary btn-sm"
          onclick={() => { showResetModal = false; }}
        >
          취소
        </button>
        <button
          class="btn btn-sm bg-error text-white hover:bg-error/90"
          disabled={isResetSubmitting}
          onclick={submitResetCompanyVerification}
        >
          {isResetSubmitting ? '처리 중...' : '초기화'}
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- 계정 삭제 확인 모달 -->
{#if showDeleteAccountModal}
  <div
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    onclick={(e) => { if (e.target === e.currentTarget && !isDeletingAccount) showDeleteAccountModal = false; }}
    role="dialog"
    aria-modal="true"
    aria-labelledby="delete-account-title"
    tabindex="-1"
    onkeydown={(e) => { if (e.key === 'Escape' && !isDeletingAccount) showDeleteAccountModal = false; }}
  >
    <div class="bg-surface rounded-2xl shadow-xl max-w-md w-full p-6">
      <h3 id="delete-account-title" class="text-h3 text-content mb-2">
        계정을 삭제할까요?
      </h3>
      <p class="text-body2 text-content-secondary mb-4">
        삭제하면 바로 로그아웃되고,<br />
        모든 기기의 로그인과 알림 수신이 끊깁니다.
      </p>
      <div class="bg-error-50 dark:bg-error/15 border border-error/30 rounded-lg p-3 mb-4">
        <p class="text-caption text-error-700 dark:text-error">
          6개월 안에 다시 로그인하면 계정을 되살릴 수 있습니다.<br />
          그 기간이 지나면 글·댓글·발표자료까지 영구 삭제되며 되돌릴 수 없습니다.
        </p>
      </div>

      <form onsubmit={(e) => { e.preventDefault(); submitDeleteAccount(); }}>
        <label for="delete_confirm" class="block text-label text-content-secondary mb-1">
          확인을 위해 <span class="text-content font-semibold">{DELETE_CONFIRM_PHRASE}</span> 를 입력해 주세요
        </label>
        <input
          id="delete_confirm"
          type="text"
          bind:value={deleteConfirmText}
          class="input"
          autocomplete="off"
          disabled={isDeletingAccount}
        />

        <div class="flex justify-end gap-2 mt-5">
          <button
            type="button"
            class="btn btn-secondary btn-sm"
            disabled={isDeletingAccount}
            onclick={() => { showDeleteAccountModal = false; }}
          >
            취소
          </button>
          <button
            type="submit"
            class="btn btn-sm bg-error text-white hover:bg-error/90"
            disabled={isDeletingAccount || deleteConfirmText.trim() !== DELETE_CONFIRM_PHRASE}
          >
            {isDeletingAccount ? '삭제 중...' : '계정 삭제'}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}

<!-- 비밀번호 변경 모달 -->
{#if showPasswordModal}
  <div
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    onclick={(e) => { if (e.target === e.currentTarget) showPasswordModal = false; }}
    role="dialog"
    aria-modal="true"
    aria-labelledby="pw-title"
    tabindex="-1"
    onkeydown={(e) => { if (e.key === 'Escape') showPasswordModal = false; }}
  >
    <div class="bg-white dark:bg-dark-secondary rounded-2xl shadow-xl max-w-md w-full p-6">
      <h3 id="pw-title" class="text-h3 text-content mb-4">비밀번호 변경</h3>

      <form onsubmit={(e) => { e.preventDefault(); submitChangePassword(); }}>
        <div class="space-y-3">
          <div>
            <label for="current_pw" class="block text-label text-content-secondary mb-1">현재 비밀번호</label>
            <input
              id="current_pw"
              type="password"
              bind:value={currentPassword}
              class="input"
              autocomplete="current-password"
              required
            />
          </div>
          <div>
            <label for="new_pw" class="block text-label text-content-secondary mb-1">새 비밀번호</label>
            <input
              id="new_pw"
              type="password"
              bind:value={newPassword}
              class="input"
              autocomplete="new-password"
              minlength="8"
              required
            />
            <p class="text-caption text-content-tertiary mt-1">8자 이상</p>
          </div>
          <div>
            <label for="confirm_pw" class="block text-label text-content-secondary mb-1">새 비밀번호 확인</label>
            <input
              id="confirm_pw"
              type="password"
              bind:value={newPasswordConfirmation}
              class="input"
              autocomplete="new-password"
              minlength="8"
              required
            />
          </div>
        </div>
        <div class="flex justify-end gap-2 mt-5">
          <button
            type="button"
            class="btn btn-secondary btn-sm"
            onclick={() => { showPasswordModal = false; }}
          >
            취소
          </button>
          <button
            type="submit"
            class="btn btn-primary btn-sm"
            disabled={isPasswordSubmitting || !currentPassword || !newPassword || newPassword !== newPasswordConfirmation}
          >
            {isPasswordSubmitting ? '변경 중...' : '비밀번호 변경'}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}
